import React, { useState, useEffect } from 'react';
import { X, Users, BarChart3, Calendar, TrendingUp, Clock, Activity, RefreshCw, Shield, Megaphone, Save, Eye } from 'lucide-react';
import { getAdminAnalytics, getModalConfig, updateModalConfig } from '../services/adminService';
import { AdminAnalytics, UserStats, AdvertisingModalConfig } from '../types';
import AdvertisingModal from './AdvertisingModal';

interface AdminDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ isOpen, onClose }) => {
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'analytics' | 'modal'>('analytics');
  
  // Modal config state
  const [modalConfig, setModalConfig] = useState<AdvertisingModalConfig | null>(null);
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [modalSaveSuccess, setModalSaveSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Error al cargar los datos de análisis');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchModalConfig = async () => {
    try {
      const config = await getModalConfig();
      setModalConfig(config);
    } catch (err) {
      console.error('Error fetching modal config:', err);
    }
  };

  const handleSaveModalConfig = async () => {
    if (!modalConfig) return;
    
    setIsSavingModal(true);
    setModalSaveSuccess(false);
    try {
      await updateModalConfig(modalConfig);
      setModalSaveSuccess(true);
      setTimeout(() => setModalSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving modal config:', err);
      alert('Error al guardar la configuración');
    } finally {
      setIsSavingModal(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAnalytics();
      fetchModalConfig();
    }
  }, [isOpen]);

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  };

  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short'
    }).format(date);
  };

  const maxDailyCount = analytics?.dailyStats 
    ? Math.max(...analytics.dailyStats.map(d => d.analysisCount), 1) 
    : 1;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div 
        className={`fixed inset-4 md:inset-10 bg-white border border-slate-200 z-50 rounded-2xl transform transition-all duration-300 ease-out shadow-2xl flex flex-col overflow-hidden ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-50 rounded-xl border border-sky-100">
              <Shield className="w-6 h-6 text-sky-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Admin Dashboard</h2>
              <p className="text-xs text-slate-500">Estadísticas y análisis de uso</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchAnalytics}
              disabled={isLoading}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refrescar datos"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-100 bg-white">
          <div className="flex gap-2 px-6">
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-4 py-3 font-medium text-sm transition-all relative ${
                activeTab === 'analytics'
                  ? 'text-sky-500'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Análisis
              </div>
              {activeTab === 'analytics' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('modal')}
              className={`px-4 py-3 font-medium text-sm transition-all relative ${
                activeTab === 'modal'
                  ? 'text-emerald-500'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                Modal Publicitario
              </div>
              {activeTab === 'modal' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          {activeTab === 'analytics' ? (
            <>
              {isLoading && !analytics ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-64 text-red-500">
                  <p>{error}</p>
                  <button 
                    onClick={fetchAnalytics}
                    className="mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              ) : analytics ? (
                <div className="space-y-8">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard 
                      icon={<Users className="w-5 h-5" />}
                      label="Usuarios Totales"
                      value={analytics.totalUsers}
                      color="sky"
                    />
                    <StatCard 
                      icon={<BarChart3 className="w-5 h-5" />}
                      label="Análisis Totales"
                      value={analytics.totalAnalyses}
                      color="emerald"
                    />
                    <StatCard 
                      icon={<Calendar className="w-5 h-5" />}
                      label="Análisis Hoy"
                      value={analytics.analysesToday}
                      color="amber"
                    />
                    <StatCard 
                      icon={<TrendingUp className="w-5 h-5" />}
                      label="Esta Semana"
                      value={analytics.analysesThisWeek}
                      color="purple"
                    />
                  </div>

                  {/* Chart - Last 30 Days */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-6">
                      <Activity className="w-5 h-5 text-sky-500" />
                      <h3 className="text-lg font-bold text-slate-800">Análisis - Últimos 30 días</h3>
                      <span className="text-sm text-slate-500 ml-auto">{analytics.analysesThisMonth} total</span>
                    </div>
                    <div className="h-48 flex items-end gap-1">
                      {analytics.dailyStats.map((day, idx) => (
                        <div 
                          key={day.date}
                          className="flex-1 flex flex-col items-center group"
                          title={`${formatShortDate(day.date)}: ${day.analysisCount} análisis`}
                        >
                          <div 
                            className="w-full bg-gradient-to-t from-sky-400 to-emerald-400 rounded-t transition-all group-hover:from-sky-500 group-hover:to-emerald-500"
                            style={{ 
                              height: `${Math.max((day.analysisCount / maxDailyCount) * 100, 2)}%`,
                              minHeight: day.analysisCount > 0 ? '4px' : '2px'
                            }}
                          />
                          {idx % 5 === 0 && (
                            <span className="text-[10px] text-slate-500 mt-2 -rotate-45 origin-left whitespace-nowrap">
                              {formatShortDate(day.date)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Users Table */}
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="flex items-center gap-2 p-6 border-b border-slate-100">
                      <Users className="w-5 h-5 text-emerald-500" />
                      <h3 className="text-lg font-bold text-slate-800">Top Usuarios</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs text-slate-500 uppercase tracking-wider bg-slate-50">
                            <th className="px-6 py-3 font-medium">#</th>
                            <th className="px-6 py-3 font-medium">Usuario</th>
                            <th className="px-6 py-3 font-medium">Email</th>
                            <th className="px-6 py-3 font-medium text-right">Análisis</th>
                            <th className="px-6 py-3 font-medium text-right">Última Actividad</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {analytics.topUsers.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                                No hay datos de usuarios todavía
                              </td>
                            </tr>
                          ) : (
                            analytics.topUsers.map((user, index) => (
                              <tr key={user.userId} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4">
                                  <span className={`text-sm font-bold ${
                                    index === 0 ? 'text-amber-500' : 
                                    index === 1 ? 'text-slate-400' : 
                                    index === 2 ? 'text-amber-700' : 'text-slate-500'
                                  }`}>
                                    {index + 1}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-slate-800 font-medium">{user.name}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-slate-500">{user.email}</span>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-200">
                                    {user.analysisCount}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex items-center justify-end gap-1 text-xs text-slate-500">
                                    <Clock className="w-3 h-3" />
                                    {formatDate(user.lastActive)}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            /* Modal Configuration Tab */
            modalConfig ? (
              <div className="space-y-6 max-w-3xl mx-auto">
                {/* Enable/Disable Toggle */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 mb-1">Activar Modal</h3>
                      <p className="text-sm text-slate-500">Mostrar modal publicitario a los usuarios</p>
                    </div>
                    <button
                      onClick={() => setModalConfig({ ...modalConfig, enabled: !modalConfig.enabled })}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                        modalConfig.enabled ? 'bg-emerald-500' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                          modalConfig.enabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Title */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Título</label>
                  <input
                    type="text"
                    value={modalConfig.title}
                    onChange={(e) => setModalConfig({ ...modalConfig, title: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                    placeholder="Título del modal"
                  />
                </div>

                {/* Message */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Mensaje</label>
                  <textarea
                    value={modalConfig.message}
                    onChange={(e) => setModalConfig({ ...modalConfig, message: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all resize-none h-32"
                    placeholder="Mensaje publicitario"
                  />
                </div>

                {/* Link Text */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Texto del Enlace (Opcional)</label>
                  <input
                    type="text"
                    value={modalConfig.linkText || ''}
                    onChange={(e) => setModalConfig({ ...modalConfig, linkText: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                    placeholder="Ej: Conocer más"
                  />
                </div>

                {/* Link URL */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <label className="block text-sm font-bold text-slate-700 mb-2">URL del Enlace (Opcional)</label>
                  <input
                    type="url"
                    value={modalConfig.linkUrl || ''}
                    onChange={(e) => setModalConfig({ ...modalConfig, linkUrl: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                    placeholder="https://ejemplo.com"
                  />
                </div>

                {/* Frequency */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Frecuencia de Aparición</label>
                  <select
                    value={modalConfig.frequency}
                    onChange={(e) => setModalConfig({ ...modalConfig, frequency: e.target.value as AdvertisingModalConfig['frequency'] })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                  >
                    <option value="always">Siempre (cada análisis)</option>
                    <option value="once_per_session">Una vez por sesión</option>
                    <option value="once_per_day">Una vez por día</option>
                    <option value="once_per_week">Una vez por semana</option>
                    <option value="every_2_analyses">Cada 2 análisis</option>
                    <option value="every_3_analyses">Cada 3 análisis</option>
                    <option value="every_5_analyses">Cada 5 análisis</option>
                  </select>
                </div>

                {/* Timeout */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Tiempo de Espera (segundos)</label>
                  <p className="text-xs text-slate-500 mb-3">Tiempo que el usuario debe esperar antes de poder cerrar el modal</p>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={modalConfig.timeoutSeconds}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      const timeoutSeconds = isNaN(value) ? 0 : Math.max(0, Math.min(60, value));
                      setModalConfig({ ...modalConfig, timeoutSeconds });
                    }}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                    placeholder="5"
                  />
                </div>

                {/* Button Text */}
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Texto del Botón</label>
                  <input
                    type="text"
                    value={modalConfig.buttonText}
                    onChange={(e) => setModalConfig({ ...modalConfig, buttonText: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                    placeholder="Ej: Continuar al resultado"
                  />
                </div>

                {/* Save and Preview Buttons */}
                <div className="flex items-center justify-between gap-3 pt-4">
                  <button
                    onClick={() => setShowPreview(true)}
                    className="bg-white hover:bg-slate-50 text-slate-700 font-bold py-3 px-8 rounded-xl transition-all duration-200 flex items-center gap-2 border border-slate-300 hover:border-slate-400 active:scale-95 shadow-sm"
                  >
                    <Eye className="w-4 h-4" />
                    Previsualizar
                  </button>
                  <div className="flex items-center gap-3">
                    {modalSaveSuccess && (
                      <span className="text-emerald-500 text-sm font-medium">¡Guardado exitosamente!</span>
                    )}
                    <button
                      onClick={handleSaveModalConfig}
                      disabled={isSavingModal}
                      className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-400 hover:to-emerald-400 text-white font-bold py-3 px-8 rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 shadow-lg shadow-sky-200"
                    >
                      {isSavingModal ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Guardar Configuración
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {modalConfig && (
        <AdvertisingModal
          isOpen={showPreview}
          config={{ ...modalConfig, enabled: true }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'sky' | 'emerald' | 'amber' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color }) => {
  const colorClasses = {
    sky: 'text-sky-500 bg-sky-50 border-sky-100',
    emerald: 'text-emerald-500 bg-emerald-50 border-emerald-100',
    amber: 'text-amber-500 bg-amber-50 border-amber-100',
    purple: 'text-purple-500 bg-purple-50 border-purple-100'
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all shadow-sm">
      <div className={`inline-flex p-2 rounded-lg border mb-3 ${colorClasses[color]}`}>
        {icon}
      </div>
      <div className="text-3xl font-black text-slate-800 mb-1">{value.toLocaleString('es-AR')}</div>
      <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">{label}</div>
    </div>
  );
};

export default AdminDashboard;
